import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import validator from 'validator';
import { toast } from 'react-toastify';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import seatInfoFormatter from '../../utils/formatters';
import { completeReservation } from '../../redux/slices/reservationsSlice';
import { openFileInNewTab } from '../../utils/fileUtils';
import { useTheme, useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import FormHelperText from '@mui/material/FormHelperText';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import LanguageSelector from '../LanguageSelector';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import api from '../../api';
import printApi from '../../printApi';
import { TIMEOUT_LOCALHOST, TIMEOUT_NONE } from '../../constants';
import { useOrderPolling } from '../../utils/useOrderPolling';
import { normalizeOptionalPhone } from '../../utils/phoneUtils';

const CheckoutStep = {
  FIRST: 1,
  SECOND: 2,
  THIRD: 3,
};

const EVENT_INITIAL_STATE = {
  title: '-',
  location: '-',
  city: '-',
  date: '',
  scan_code: '',
  is_one_plus_one_free: false,
  is_three_plus_one_free: false,
  premiere: false,
  national_premiere: false,
  second_representation: false,
  platform_comission: 0,
  seat_price: 0,
  comission_cash_per_seat: false,
  notification_sms_price: 0,
  notifiction_email_price: 0,
  status: 'SELLING',
  can_delete: false,
  index_for_search: true,
};

const formConfig = () => ({
  firstName: {
    value: '',
    error: false,
    errorText: '',
    isValid: (value) => {
      return true;
    },
  },

  lastName: {
    value: '',
    error: false,
    errorText: '',
    isValid: (value) => {
      return true;
    },
  },

  email: {
    value: '',
    error: false,
    errorText: 'Invalid email',
    isValid: (value) => {
      return validator.isEmpty(value, { ignore_whitespace: true }) || validator.isEmail(value);
    },
  },

  phone: {
    value: '',
    error: false,
    errorText: 'Invalid phone',
    isValid: (value) => {
      return normalizeOptionalPhone(value).isValid;
    },
  },

  paymentType: {
    value: '',
    error: false,
    errorText: 'Payment method empty',
    isValid: (value) => {
      return ['CASH', 'CARD'].includes(value);
    },
  },

  receiptNr: {
    value: '',
    error: false,
    errorText: '',
    isValid: (value) => {
      return true;
    },
  },
  language: {
    value: 'ro',
    error: false,
    errorText: '',
    isValid: (value) => {
      return ['en', 'de', 'ro', 'hu'].includes(value);
    },
  },
});

const FirstStepContent = ({ event, seats, formState, onInputChange }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <>
      <Typography component="h2" variant="h6">
        {t('Client details')}
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid item xs={6}>
          <TextField
            name="firstName"
            value={formState.firstName.value}
            onChange={onInputChange}
            error={formState.firstName.error}
            helperText={formState.firstName.error ? formState.firstName.errorText : ''}
            fullWidth
            variant="standard"
            type="text"
            label={t('First name')}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="lastName"
            value={formState.lastName.value}
            onChange={onInputChange}
            error={formState.lastName.error}
            helperText={formState.lastName.error ? formState.lastName.errorText : ''}
            fullWidth
            variant="standard"
            type="text"
            label={t('Last name')}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="email"
            value={formState.email.value}
            onChange={onInputChange}
            error={formState.email.error}
            helperText={formState.email.error ? formState.email.errorText : ''}
            fullWidth
            variant="standard"
            type="email"
            label={t('Email')}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            value={formState.phone.value}
            onChange={onInputChange}
            error={formState.phone.error}
            helperText={formState.phone.error ? formState.phone.errorText : ''}
            name="phone"
            fullWidth
            variant="standard"
            type="tel"
            label={t('Phone')}
          />
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        {t('languageSelectInfo')}
      </Typography>

      <LanguageSelector
        language={formState.language.value}
        setLanguage={(newLang) =>
          onInputChange({
            target: { name: 'language', value: newLang },
          })
        }
      />
      <Typography sx={{ mt: 2 }} gutterBottom variant="h6">
        {t('Event Details')}
      </Typography>
      <Typography variant="subtitle1">
        {event.title}
        {t('-')}
        {dayjs(event.date).format('dddd DD MMMM YYYY HH:mm')}
      </Typography>
      <Typography gutterBottom variant="subtitle1">
        {event.location}
      </Typography>
      <Typography gutterBottom variant="subtitle1">
        {event.city}
      </Typography>
      <Divider />
      {seats.length ? (
        <List sx={{ maxHeight: 200, overflow: 'auto' }}>
          {seats.map(
            (
              { id, typeLabel, isDefaultType, section, row, seat, basePrice, discount, price, isFree, categoryLabel },
              index
            ) => (
              <ListItem disablePadding key={`${id}-${index}`}>
                <ListItemText
                  primary={
                    <>
                      {categoryLabel}
                      {!isDefaultType && <Chip sx={{ marginLeft: 1 }} size="small" label={typeLabel} />}
                    </>
                  }
                  secondary={seatInfoFormatter(section, row, seat, t)}
                />
                <ListItemSecondaryAction sx={{ right: 0 }}>
                  {isFree || discount ? (
                    <Stack>
                      <span style={{ textDecoration: 'line-through', fontSize: '0.70rem' }}>
                        {(isFree ? price : basePrice).toFixed(2) + ' Lei'}
                      </span>
                      <span style={{ color: theme.palette.error.main }}>
                        {(isFree ? 0 : price).toFixed(2) + ' Lei'}
                      </span>
                    </Stack>
                  ) : (
                    price.toFixed(2) + ' Lei'
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            )
          )}
        </List>
      ) : (
        <Box sx={{ height: '100px' }} display="flex" alignItems="center" justifyContent="center">
          <Typography variant="subtitle1">{t('No seat selected')}</Typography>
        </Box>
      )}
      <Divider />
    </>
  );
};

const FirstStepFooter = ({ isLoading, totalPrice, seats, onAction, actionText }) => {
  const { t } = useTranslation();
  return (
    <Grid sx={{ mt: 2 }} item container justifyContent="space-between" alignItems="center">
      <Grid item>
        <Typography component="h2" variant="h6">
          {t('Total:')}
          {totalPrice.toFixed(2) + ' Lei'}
        </Typography>
      </Grid>
      <Grid item>
        <Button disabled={isLoading || !seats.length} onClick={onAction} variant="contained">
          {actionText}
          {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </Grid>
    </Grid>
  );
};

const SecondStepContent = ({ formState, onInputChange }) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" width="100%">
      <DialogContentText gutterBottom sx={{ textAlign: 'center', fontSize: 'large' }}>
        {t('After a successful payment press "Complete"')}
      </DialogContentText>
      <DialogContentText gutterBottom sx={{ fontSize: 'large', fontWeight: 'bold' }}>
        {t('Payment method:')}
        {formState.paymentType.value}
      </DialogContentText>
      {formState.paymentType.value === 'CARD' && (
        <TextField
          name="receiptNr"
          value={formState.receiptNr.value}
          onChange={onInputChange}
          error={formState.receiptNr.error}
          helperText={formState.receiptNr.error ? formState.receiptNr.errorText : ''}
          variant="standard"
          type="text"
          label={t('Receipt No.')}
        />
      )}
    </Box>
  );
};

const SecondStepFooter = ({ totalPrice, seats, isLoading, onBack, onAction }) => {
  const { t } = useTranslation();

  return (
    <Grid item container justifyContent="space-between" alignItems="center">
      <Grid item>
        <Typography component="h2" variant="h6">
          {t('Total:')}
          {totalPrice.toFixed(2) + ' Lei'}
        </Typography>
      </Grid>
      <Grid item>
        <Button sx={{ marginRight: 1 }} disabled={isLoading} onClick={onBack} variant="contained">
          {t('Back')}
        </Button>
        <Button disabled={isLoading || !seats.length} onClick={onAction} variant="contained">
          {t('Complete')}
          {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </Grid>
    </Grid>
  );
};

const ThirdStepContent = ({ orderReference, formLanguage }) => {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState('OFFLINE');
  const [isLoadingPrinterStatus, setIsLoadingPrinterStatus] = useState(false);
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [isPrinterConnected, setIsPrinterConnected] = useState(false);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [language, setLanguage] = useState(formLanguage);

  const { isPolling, retryLimitReached } = useOrderPolling(orderReference);

  useEffect(() => {
    if (!orderReference || selectedType !== 'PRINT') return;
    setIsLoadingPrinterStatus(true);

    printApi
      .get('/health', { timeout: TIMEOUT_LOCALHOST })
      .then(async (res) => {
        setIsServerReachable(true);
        res.data.ok ? setIsPrinterConnected(true) : setIsPrinterConnected(false);
        setIsLoadingPrinterStatus(false);
      })
      .catch((err) => {
        setIsLoadingPrinterStatus(false);
        setIsServerReachable(false);
      });
  }, [orderReference, t, selectedType]);

  const downloadTickets = () => {
    api
      .get(`/orders/${orderReference}/tickets?type=${selectedType}${`&language=${language}`}`, {
        timeout: TIMEOUT_NONE,
        responseType: 'blob',
      })
      .then((res) => {
        const { data } = res;
        const tabWindow = window.open('', '_blank');
        openFileInNewTab(tabWindow, data, 'application/pdf');
      })
      .catch((err) => {
        console.log('Error fetching data ', err);
        toast.error(t('An error occured please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };

  const printTickets = () => {
    setIsLoadingPrint(true);

    api
      .get(`/orders/${orderReference}/ticketsZip?type=${selectedType}${`&language=${language}`}`, {
        timeout: TIMEOUT_NONE,
        responseType: 'blob',
      })
      .then(async (res) => {
        const zipBlob = res.data;

        const formData = new FormData();
        formData.append('zip_file', zipBlob, 'tickets.zip');

        const printRes = await printApi.post('/print', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!printRes.status || printRes.status !== 200) {
          throw new Error('Failed to print');
        }
        setIsLoadingPrint(false);
      })
      .catch(() => {
        setIsLoadingPrint(false);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" width="100%">
      {orderReference && (
        <>
          <Box display="flex" alignItems="center" gap={2} sx={{ marginTop: 4 }}>
            <TextField
              sx={{ width: 221 }}
              disabled={isPolling || retryLimitReached}
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
              select
              label={t('Ticket format')}
            >
              <MenuItem value={'OFFLINE'}>{t('Offline')}</MenuItem>
              <MenuItem value={'ONLINE'}>{t('OnlineTicket')}</MenuItem>
              <MenuItem value={'PRINT'}>{t('ThermalPrinter')}</MenuItem>
            </TextField>
            <LanguageSelector language={language} setLanguage={setLanguage} />
          </Box>
          {(!isPrinterConnected || !isServerReachable) && selectedType === 'PRINT' && !isLoadingPrinterStatus && (
            <FormHelperText error sx={{ mt: 1 }}>
              {!isServerReachable ? t('ServerNotConnected') : !isPrinterConnected ? t('PrinterNotConnected') : ''}
            </FormHelperText>
          )}
          {isLoadingPrinterStatus && <FormHelperText sx={{ mt: 1 }}>{t('PrinterIsConnecting')}</FormHelperText>}
          {selectedType === 'PRINT' ? (
            <Button
              sx={{ marginTop: 4 }}
              disabled={isPolling || retryLimitReached || isLoadingPrint || !isPrinterConnected || !isServerReachable}
              onClick={printTickets}
              variant="contained"
            >
              {(isPolling || isLoadingPrint) && <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />}
              {isPolling ? t('Processing') : t('Print')}
            </Button>
          ) : (
            <Button
              sx={{ marginTop: 4 }}
              disabled={isPolling || retryLimitReached}
              size="large"
              width={221}
              variant="contained"
              onClick={downloadTickets}
            >
              {isPolling && <CircularProgress size={20} sx={{ mr: 1, color: '#868686' }} />}
              {isPolling ? t('Processing') : t('Download tickets')}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

const ThirdStepFooter = () => {
  return <></>;
};

function CompleteDialog({ isEventScope, open, onClose, reservation }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [step, setStep] = useState(CheckoutStep.FIRST);
  const [seats, setSeats] = useState([]);
  const [orderReference, setOrderReference] = useState(null);
  const [event, setEvent] = useState(JSON.parse(JSON.stringify(EVENT_INITIAL_STATE)));
  // eslint-disable-next-line no-unused-vars
  const [categoryPricings, setCategoryPricings] = useState([]);

  //const [isEventPending, setEventPending] = useState(true);
  //const [isPricingsPending, setPricingsPending] = useState(true);

  const isLoading = useSelector((state) => state.reservations.isLoading);

  const totalPrice = useMemo(() => seats.reduce((acc, s) => acc + (!s.isFree ? s.price : 0), 0), [seats]);
  const objectIds = useMemo(
    () =>
      seats.map(({ id, typeId, price }) => ({
        id,
        typeId,
        expectedPrice: price,
      })),
    [seats]
  );

  const [formState, setFormState] = useState(formConfig());

  const loadReservation = async (event, categoryPricings, reservation) => {
    const { is_one_plus_one_free, is_three_plus_one_free } = event;
    const { first_name, last_name, email, phone, cart } = reservation;
    const { seats } = cart;

    const objByCatCount = {};
    const pricedSeats = seats.map((seatObj) => {
      const { id, section, parent: row, own: seat, categoryId, typeId } = seatObj;
      const catPricing = categoryPricings.find(
        (c) => c.categories_id === seatObj.categoryId && c.type_id === seatObj.typeId
      );
      const {
        type_label: typeLabel,
        is_default_type: isDefaultType,
        name: categoryLabel,
        price,
        discount,
      } = catPricing;
      let isFree = false;
      const numPrice = Number(price);
      const numDiscount = Number(discount);

      const catTypeId = `${categoryId}-${typeId}`;
      objByCatCount[catTypeId] = (objByCatCount[catTypeId] || 0) + 1;

      if (
        (is_one_plus_one_free && objByCatCount[catTypeId] % 2 === 0) ||
        (is_three_plus_one_free && objByCatCount[catTypeId] % 4 === 0)
      ) {
        isFree = true;
      }

      return {
        id,
        typeId,
        typeLabel,
        isDefaultType,
        section,
        row,
        seat,
        basePrice: numPrice,
        discount: numDiscount,
        price: numPrice - numPrice * numDiscount,
        isFree,
        categoryLabel,
      };
    });

    setSeats(pricedSeats);
    setFormState((currentState) => ({
      ...currentState,
      firstName: {
        ...currentState.firstName,
        ...(first_name !== null ? { value: first_name } : {}),
      },
      lastName: {
        ...currentState.lastName,
        ...(last_name !== null ? { value: last_name } : {}),
      },
      email: {
        ...currentState.email,
        ...(email !== null ? { value: email } : {}),
      },
      phone: {
        ...currentState.phone,
        ...(phone !== null ? { value: phone } : {}),
      },
    }));
  };

  useEffect(() => {
    if (reservation) {
      const eventsPromise = api.get(`/events/${reservation.events_id}`).then((res) => {
        if (res.data && res.data.data && res.data.data.length > 0) {
          setEvent(res.data.data[0]);
          return res.data.data[0];
        }
      });
      const pricingsPromise = api
        .get(`/events/${reservation.events_id}/categoryPricings?sales_channel=cashier`)
        .then((res) => {
          if (res.data && res.data.data) {
            setCategoryPricings(res.data.data);
            return res.data.data;
          }
        });

      Promise.all([eventsPromise, pricingsPromise])
        .then(([event, categoryPricings]) => {
          loadReservation(event, categoryPricings, reservation);
        })
        .catch((err) => {
          console.log('Error fetching data ', err);
          toast.error(t('An error occured please try again!'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        });
    }
  }, [reservation, t]);

  const dispatchCompleteReservation = (payload) => {
    dispatch(completeReservation(payload))
      .unwrap()
      .then((res) => {
        if (res.data && res.data.orderReference) {
          setOrderReference(res.data.orderReference);
          setStep(CheckoutStep.THIRD);
        }
      })
      .catch((err) => {
        if (err.data && err.data.errorType && err.data.errorType === 'INVALID_EMAIL_DOMAIN_ERROR') {
          const newState = Object.assign({}, formState);
          newState['email'].error = true;
          setFormState(newState);
          setStep(CheckoutStep.FIRST);
        }
      });
  };

  const onInputChange = (input) => {
    setFormState((currentState) => ({
      ...currentState,
      [input.target.name]: {
        ...currentState[input.target.name],
        value: input.target.type === 'checkbox' ? input.target.checked : input.target.value,
        error: false,
      },
    }));
  };

  const onBackCashier = () => {
    setStep(CheckoutStep.FIRST);
  };

  const onNextCashier = () => {
    const inputs = Object.keys(formState);
    const newState = Object.assign({}, formState);

    let hasError = false;
    for (const input of inputs) {
      if (input && !newState[input].isValid(newState[input].value)) {
        hasError = true;
        newState[input].error = true;
      }
    }

    setFormState(newState);

    if (!hasError) {
      setStep(CheckoutStep.SECOND);
    }
  };

  const onFinishCashier = () => {
    const newState = Object.assign({}, formState);

    let hasError = false;
    if (newState['paymentType'].value === 'CARD' && !newState['receiptNr'].isValid(newState['receiptNr'].value)) {
      hasError = true;
      newState['receiptNr'].error = true;
    }

    setFormState(newState);

    if (!hasError) {
      const payload = {
        ...(isEventScope ? { event_id: event.id } : {}),
        payment_type: newState.paymentType.value,
        receipt_nr: newState.receiptNr.value,
        reservation_id: reservation.id,
        user: {
          email: newState.email.value,
          first_name: newState.firstName.value,
          last_name: newState.lastName.value,
          phone: normalizeOptionalPhone(newState.phone.value).value,
        },
        object_ids: objectIds,
        language: newState.language.value,
      };
      dispatchCompleteReservation(payload);
    }
  };

  const onCloseInternal = () => {
    setFormState(formConfig());
    setOrderReference(null);
    setSeats([]);
    setStep(CheckoutStep.FIRST);
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" fullScreen={fullScreen} open={open}>
      <DialogTitle gutterBottom>
        <Box display="flex" alignItems="center">
          <ScheduleIcon sx={{ marginRight: 1 }} />
          {t('Complete Reservation')}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {step === CheckoutStep.FIRST ? (
          <FirstStepContent {...{ event, seats, formState, onInputChange }} />
        ) : step === CheckoutStep.SECOND ? (
          <SecondStepContent {...{ formState, onInputChange }} />
        ) : (
          <ThirdStepContent orderReference={orderReference} formLanguage={formState.language.value} />
        )}
      </DialogContent>
      <DialogActions sx={{ paddingTop: 0, paddingLeft: 2.5, paddingRight: 2.5, paddingBottom: 2.5 }}>
        {step === CheckoutStep.FIRST ? (
          <Grid container direction="column">
            <Grid item>
              <TextField
                name="paymentType"
                required
                value={formState.paymentType.value}
                onChange={onInputChange}
                fullWidth
                select
                variant="standard"
                label={t('Payment type')}
                error={formState.paymentType.error}
                helperText={formState.paymentType.error ? formState.paymentType.errorText : ''}
              >
                <MenuItem value={'CASH'}>{t('CASH')}</MenuItem>
                <MenuItem value={'CARD'}>{t('CARD')}</MenuItem>
              </TextField>
            </Grid>
            <FirstStepFooter
              {...{
                isLoading,
                totalPrice,
                seats,
                formState,
                onInputChange,
                onAction: onNextCashier,
                actionText: t('Continue'),
              }}
            />
          </Grid>
        ) : step === CheckoutStep.SECOND ? (
          <SecondStepFooter {...{ isLoading, totalPrice, seats, onBack: onBackCashier, onAction: onFinishCashier }} />
        ) : (
          <ThirdStepFooter onAction={onCloseInternal} />
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CompleteDialog;
